import { lazy, Suspense } from "react";
import type { RouteObject } from "react-router";
import { Outlet, Navigate } from "react-router-dom";
import ScrollToTop from "src/components/scroll-to-top";
import useUserDataProvider from "src/contexts/snug/userData-context";

import { Layout as DashboardLayout } from "src/layouts/dashboard";

const IndexPage = lazy(() => import("src/pages/dashboard/index"));
const CustomerListPage = lazy(
  () => import("src/pages/dashboard/customers/list")
);
// const PrintListPage = lazy(
//   () => import("src/pages/dashboard/customers/print-list")
// );
const LearnPage = lazy(() => import("src/pages/dashboard/learn"));
const CustomerCreatePage = lazy(
  () => import("src/pages/dashboard/customers/create")
);
// const PrintCreatePage = lazy(
//   () => import("src/pages/dashboard/customers/print-create")
// );
const CustomerAnalysisPage = lazy(
  () => import("src/pages/dashboard/customers/analysis")
);
const ToolkitPage = lazy(() => import("src/pages/dashboard/toolkit"));
const ToolkitPageCustom = lazy(() => import("src/pages/dashboard/toolkit-custom"));
const MarketingPage = lazy(() => import("src/pages/dashboard/marketing"));
const QuizPage = lazy(() => import("src/pages/dashboard/quiz"));
const ReferralsPage = lazy(() => import("src/pages/dashboard/referrals"));
const UpgradePage = lazy(() => import("src/pages/dashboard/upgrade"));
const AnalysisDetail = lazy(
  () => import("src/pages/dashboard/jobs/companies/analysis")
);
const PlansDetail = lazy(
  () => import("src/pages/dashboard/jobs/companies/plans")
);
const AccountPage = lazy(() => import("src/pages/dashboard/account"));
const TransactionsPage = lazy(() => import("src/pages/dashboard/customers/transactions"));
const QuitclaimDeedsPage = lazy(() => import("src/pages/dashboard/quitclaim-deeds"));
const ScenariosPage = lazy(() => import("src/pages/dashboard/scenarios"));
const CourseDetailsPage = lazy(() => import("src/pages/dashboard/tutorial"));
const CourseDetailsPageCustom = lazy(() => import("src/pages/dashboard/tutorial-custom"));

const ProtectedScenariosPage = () => {
  const { professionalGroup } = useUserDataProvider();
  
  if (!professionalGroup?.allow_scenarios) {
    return <Navigate to="/" replace />;
  }
  
  return <ScenariosPage />;
};

const ProtectedMarketingPage = () => {
  const { professionalGroup } = useUserDataProvider();
  
  if (!professionalGroup?.allow_marketing) {
    return <Navigate to="/" replace />;
  }
  return <MarketingPage />;
};
const ProtectedAnalysisPage = () => {
  const { professionalGroup } = useUserDataProvider();
  
  if (!professionalGroup?.allow_analysis) {
    return <Navigate to="/" replace />;
  }
  return <CustomerAnalysisPage />;
};
// protect referrals page
const ProtectedReferralsPage = () => {
  const { professionalGroup } = useUserDataProvider();
  
  if (!professionalGroup?.allow_referrals) {
    return <Navigate to="/" replace />;
  }
  return <ReferralsPage />;
};
// protect quitclaim deeds page
const ProtectedQuitclaimDeedsPage = () => {
  const { professionalGroup } = useUserDataProvider();
  
  if (!professionalGroup?.allow_quitclaim_deed) {
    return <Navigate to="/" replace />;
  }
  return <QuitclaimDeedsPage />;
};

export const dashboardRoutes: RouteObject[] = [
  {
    path: "/", // Change this to the desired base path
    element: (
      <DashboardLayout>
        <Suspense>
          <ScrollToTop />
          <Outlet />
        </Suspense>
      </DashboardLayout>
    ),
    children: [
      {
        index: true,
        element: <IndexPage />,
      },
      {
        path: "clients",
        children: [
          {
            path: "create",
            element: <CustomerCreatePage />,
          },
          {
            path: "analysis",
            element: <ProtectedAnalysisPage />,
          },
          // {
          //   path: ":advisorClientId/upload",
          //   element: <CustomerUploadPage />,
          // },
          {
            index: true,
            element: <CustomerListPage />,
          },
          // {
          //   path: "print",
          //   element: <PrintListPage />,
          // },
          // {
          //   path: "print/create",
          //   element: <PrintCreatePage />,
          // },
        ],
      },
      {
        path: "transactions",
        element: <TransactionsPage />,
      },
      {
        path: "quitclaim-deeds",
        element: <ProtectedQuitclaimDeedsPage />,
      },
      {
        path: "account/:tab?",
        element: <AccountPage />,
      },
      {
        path: "scenarios",
        element: <ProtectedScenariosPage />,
      },
      {
        path: "tutorials",
        element: <LearnPage />,
      },
      {
        path: "tutorials/:id", // This will handle individual course details
        element: <CourseDetailsPage />, // This is your CourseDetailsPage component
      },
      {
        path: "demo", // This will handle individual course details
        element: <CourseDetailsPageCustom />, // This is your CourseDetailsPage component
      },
      {
        path: "toolkit",
        element: <ToolkitPage />,
      },
      {
        path: "protoolkit",
        element: <ToolkitPageCustom />,
      },
      {
        path: "referrals",
        element: <ProtectedReferralsPage />,
      },
      {
        path: "upgrade",
        element: <UpgradePage />,
      },
      {
        path: "marketing",
        element: <ProtectedMarketingPage />,
      },
      {
        path: "quiz",
        element: <QuizPage />,
      },
      {
        path: "learn/plans",
        element: <PlansDetail />,
      },
      {
        path: "learn/analysis",
        element: <AnalysisDetail />,
      },
    ],
  },
];
